import { useMemo } from 'react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import _ from 'lodash';
import { SgDateUtil } from 'saga-modules';
import moment from 'moment';
import SgSptConstants from 'sg-spt-constants';

const UK_COUNTRIES_CODES = [
    SgSptConstants.COUNTRY_CODES.CHANNEL_ISLANDS,
    SgSptConstants.COUNTRY_CODES.GUERNSEY,
    SgSptConstants.COUNTRY_CODES.WALES,
    SgSptConstants.COUNTRY_CODES.ISLE_OF_MAN,
    SgSptConstants.COUNTRY_CODES.SCOTLAND,
    SgSptConstants.COUNTRY_CODES.NORTHERN_IRELAND,
    SgSptConstants.COUNTRY_CODES.SCILLY_ISLES,
    SgSptConstants.COUNTRY_CODES.JERSEY,
    SgSptConstants.COUNTRY_CODES.UK,
];

function getAllCountries(date) {
    let csd = date;
    if (SgDateUtil.dateIsValid(csd)) {
        csd = moment(SgDateUtil.toDate(csd));
    } else {
        csd = moment();
    }
    return appConfig.travelCountries.filter((destination) => {
        return moment(SgDateUtil.toDate(destination.effDateStart)).isSameOrBefore(moment(csd))
            && moment(SgDateUtil.toDate(destination.effDateEnd)).isSameOrAfter(csd);
    });
}

function getAvailableCountriesBySeason(availableCountries) {
    const csd = moment();
    const countriesAvailableFromOctoberUntilEOY = [
        SgSptConstants.COUNTRY_CODES.SPAIN,
        SgSptConstants.COUNTRY_CODES.USA,
        SgSptConstants.COUNTRY_CODES.FRANCE,
        SgSptConstants.COUNTRY_CODES.AUSTRALIA,
        SgSptConstants.COUNTRY_CODES.TENERIFE
    ];
    const countriesAvailableFromStartOfYearUntilOctober = [
        SgSptConstants.COUNTRY_CODES.SPAIN,
        SgSptConstants.COUNTRY_CODES.USA,
        SgSptConstants.COUNTRY_CODES.FRANCE,
        SgSptConstants.COUNTRY_CODES.ITALY,
        SgSptConstants.COUNTRY_CODES.PORTUGAL
    ];
    const countries = (csd.month() >= 9) ? countriesAvailableFromOctoberUntilEOY : countriesAvailableFromStartOfYearUntilOctober;
    const result = availableCountries.filter((el) => countries.includes(el.code))
        .sort((a, b) => countries.indexOf(a.code) - countries.indexOf(b.code));
    result.push({
        code: SgSptConstants.COUNTRY_CODES.OTHER,
        name: 'Other destinations'
    });
    return result;
}

function useSgSptCountries(model) {
    return useMemo(() => {
        const allCountries = getAllCountries(model.baseData.periodStartDate);
        const europeanCountries = allCountries.filter((el) => el.region === SgSptConstants.REGIONS.EUROPE);
        const availableCountries = allCountries.map((el) => {
            return {
                code: el.destinationCode,
                name: el.destinationName
            };
        });
        return {
            availableCountries: availableCountries,
            availableCountriesBySeason: getAvailableCountriesBySeason(availableCountries),
            euCountryNames: europeanCountries.map((el) => el.destinationName),
            worldwideCountryNames: allCountries.filter((el) => el.region !== SgSptConstants.REGIONS.EUROPE)
                .map((el) => el.destinationName),
            woiCountryNames: allCountries.filter((el) => el.region === SgSptConstants.REGIONS.WORLDWIDE_INCL)
                .map((el) => el.destinationName),
            hasOnlyEuropeanCountries: (countries) => {
                if (!countries || !countries.length) {
                    return false;
                }
                const euCountryCodes = europeanCountries.map((el) => el.destinationCode);
                return _.every(countries, (country) => {
                    return euCountryCodes.includes(country.destination);
                });
            },
            hasUKCountries: (countries) => {
                if (!countries || !countries.length) {
                    return false;
                }
                return countries.some((country) => UK_COUNTRIES_CODES.includes(country.destination));
            },
            hasOnlyUKCountries: (countries) => {
                if (!countries || !countries.length) {
                    return false;
                }
                return countries.every((country) => UK_COUNTRIES_CODES.includes(country.destination));
            },
            findRegionByCountry: (country) => {
                return allCountries.find((c) => c.destinationCode === country.code).region;
            }
        };
    }, [model.baseData.periodStartDate]);
}

export default useSgSptCountries;
